import { useEffect, useState } from "react";
import { 
  Save, 
  MessageSquare, 
  RefreshCw, 
  Send, 
  Phone, 
  CheckCircle2, 
  AlertTriangle, 
  XCircle, 
  Loader2,
  Sparkles,
  Globe
} from "lucide-react";
import AdminLayout from "@/components/AdminLayout";
import api, { getSettings, saveSettings, getProperties } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";

// Default message templates
const DEFAULT_TEMPLATES = {
  // ================= FOOD ORDERS =================
  msg_food_order_staff: `🍽️ *New Food Order*
*Guest:* {guest_name}
*Room:* {room_number}
*Order:* {items}
*Notes:* {notes}`,

  msg_food_pay_success_staff: `✅ *Payment Completed (Order #{order_id})*
*Room:* {room_number}
*Order:* {items}
*Amount:* ₹{amount}
*Fulfillment OTP:* {fulfillment_code}`,

  msg_food_pay_success_manager: `✅ *Payment Completed (Order #{order_id})*
*Room:* {room_number}
*Order:* {items}
*Amount:* ₹{amount}
*Fulfillment OTP:* {fulfillment_code}`,

  msg_food_order_reception: `🍽️ *Order Accepted by Kitchen!*

Namaste {guest_name}, your food order (#{order_id}) has been accepted and is being prepared fresh. 
*Note:* Meals typically take 30-45 minutes.

Payment of ₹{total} will be collected at reception.

Thanks, Team Hidden Monkey Stays.`,

  msg_food_order_guest_approved: `🍽️ *Order Accepted by Kitchen!*

Namaste {guest_name} 🐒
Your order (#{order_id}) is accepted and is being prepared fresh. 
*Note:* Meals typically take 30-45 minutes to reach your room. We will dispatch it as soon as it's ready!

Please complete your payment of ₹{amount} using the secure link below:
{payment_link}`,

  msg_food_order_guest_rejected: `❌ *Order Update*

Namaste {guest_name}, 
We are truly sorry, but we are unable to fulfill your food order (#{order_id}) at this moment due to kitchen capacity. Please contact the front desk if you need alternative recommendations!`,

  msg_order_payment_failed_guest: `⚠️ *Payment Update*

Hi {guest_name}, it looks like the payment for order #{order_id} did not go through. 

Please try the link again or speak to our front desk for assistance!`,

  msg_food_pay_failed_staff: `⚠️ *Payment Failed — Order #{order_id}*
Guest: {guest_name} | Room: {room_number}
Amount due: ₹{amount}
Online payment failed. Please ask the guest to pay ₹{amount} at reception.`,

  msg_broadcast_event: `🎉 *Event Tonight at Hidden Monkey!*

Namaste {guest_name}! We're excited to share —

*{event_name}* is happening today at *{event_time}* 🐒

Hope to see you there! Let us know if you have any questions.

— The Hidden Monkey Team`,

  msg_broadcast_alert: `ℹ️ *Update from Hidden Monkey*

Namaste {guest_name}, a quick heads up —

{alert_message}

Please let us know if you need any help. We're here for you 🐒

— The Hidden Monkey Team`,

  msg_food_pay_success_guest: `✅ *Payment Confirmed!*

Hi {guest_name}, we have successfully received your payment for order #{order_id}. Sit back and relax, our kitchen is preparing your order fresh and we will deliver it to your room as soon as it's ready!`,

  // ================= EXPERIENCE BOOKINGS =================
  msg_vendor_broadcast: `🚨 *New Booking Request #{order_id}*
*Experience:* {experience_title}
*Guest:* {guest_name} (Room {room_number})
━━━━━━━━━━━━━━━
First to claim gets the booking. Reply to confirm availability.`,

  msg_booking_staff: `🎯 *New Experience Booking*
*Guest:* {guest_name}
*Experience:* {experience_title}
*Details:* {date_slots}
*Notes:* {notes}`,

  msg_booking_guest_approved: `✅ *Booking Approved!*

Namaste {guest_name}, your booking for {experience_title} is confirmed and our partners are excited to host you!

Please secure your booking by completing the payment of ₹{amount} here: 
{payment_link}`,

  msg_booking_guest_rejected: `❌ *Booking Update*

Namaste {guest_name}, we are so sorry, but your booking for {experience_title} could not be confirmed at this time.
Reason: {reason}

Please speak to our front desk team for alternative times or experiences!`,

  msg_booking_payment_failed_guest: `⚠️ *Booking Payment Update*

Namaste {guest_name}, it looks like the payment for your {experience_title} booking could not be completed.

Please try the link again or contact our front desk team for assistance!`,

  msg_booking_payment_staff: `💳 *PAYMENT RECEIVED*
Payment from {guest_name} for {experience_title} is confirmed. Please ensure the vendor is coordinated.`,

  msg_booking_payment_guest: `💳 *Payment Confirmed!*

Hi {guest_name}, your payment of ₹{amount} for {experience_title} (ID: #{order_id}) was successful! We hope you have a fantastic experience.`,

  msg_booking_payment_success_guest: `💳 *Payment Confirmed!*

Hi {guest_name}, your payment of ₹{amount} for {experience_title} (ID: #{order_id}) was successful! We hope you have a fantastic experience.`,

  // ================= SERVICE REQUESTS =================
  msg_request_staff: `📋 *New Service Request*
*Guest:* {guest_name}
*Category:* {category}
*Room:* {room_number}
*Message:* {message}`,

  msg_request_guest_received: `📋 *Request Received!*

Namaste {guest_name}, we have received your {category} request (Ref: #{order_id}). Our team has been notified.
If this is an absolute emergency, please call the manager directly at {manager_phone}.`,

  msg_request_guest_acknowledged: `✅ *Request Acknowledged!*

Hi {guest_name}, our staff is actively working on your {category} request (Ref: #{order_id}). We will have this sorted for you as quickly as possible!`,

  msg_request_guest_resolved: `🎉 *Request Resolved!*

Hi {guest_name}, your {category} request has been marked as completed! Please let us know if you need absolutely anything else to make your stay comfortable. 🐒`,

  // ================= CHECK-IN & WELCOME =================
  guest_checkin: `Namaste {guest_name}! Welcome to {property_name}. Access your personal guest portal here: {portal_url}`,

  guest_checkin_manager_notification: `🔔 *New Guest Check-in*
*Guest:* {guest_name}
*Phone:* {whatsapp}
*Email:* {email}`,

  // ================= AUTHENTICATION & SECURITY =================
  msg_password_reset_otp: `🔐 *Reset OTP:* Your code is {otp} to reset your password. Valid for 10 minutes.`,
  msg_food_otp: `🔐 *Order Verification:* Your code for ordering food at {property_name} is {otp}. Valid for 10 mins.`
};

const TEMPLATE_CATEGORIES = [
  {
    id: "food",
    title: "Food Orders",
    description: "Messages for food ordering flow",
    templates: [
      { key: "msg_food_order_staff", twilio_key: "twilio_tpl_food_order_staff", label: "New Order → Monkey (Kitchen)", description: "Sent to kitchen monkey when new food order is placed" },
      { key: "msg_food_order_staff_hi", twilio_key: "twilio_tpl_food_order_staff_hi", label: "New Order → Monkey (Hindi)", description: "Sent to kitchen monkey (Hindi template)" },
      { key: "msg_food_pay_success_staff", twilio_key: "twilio_tpl_food_pay_success_staff", label: "Payment Success → Monkey", description: "Notifies kitchen of payment" },
      { key: "msg_food_pay_success_staff_hi", twilio_key: "twilio_tpl_food_pay_success_staff_hi", label: "Payment Success → Monkey (Hindi)", description: "Notifies kitchen of payment (Hindi)" },
      { key: "msg_food_pay_success_manager", twilio_key: "twilio_tpl_food_pay_success_manager", label: "Payment Success → Manager", description: "Notifies manager of payment with OTP" },
      { key: "msg_food_order_guest", twilio_key: "twilio_tpl_order_approved_guest", label: "Order Approved (Payment Link) → Guest", description: "Payment link sent to guest after approval" },
      { key: "msg_food_order_reception", twilio_key: "twilio_tpl_order_approved_reception", label: "Order Approved (Pay at Reception) → Guest", description: "Fallback sent to guest if Razorpay fails or cash payment" },
      { key: "msg_order_rejected_guest", twilio_key: "twilio_tpl_order_rejected_guest", label: "Order Rejected → Guest", description: "Sent when kitchen rejects order" },
      { key: "msg_order_payment_failed_guest", twilio_key: "twilio_tpl_order_payment_failed_guest", label: "Payment Failed → Guest", description: "Sent to guest if online payment fails" },
      { key: "msg_food_pay_failed_staff", twilio_key: "twilio_tpl_food_pay_failed_staff", label: "Payment Failed → Kitchen & Manager", description: "Alerts kitchen and manager when guest payment fails — includes amount to collect at reception" },
      { key: "msg_order_payment_success_guest", twilio_key: "twilio_tpl_order_payment_success_guest", label: "Payment Success (Receipt) → Guest", description: "Payment confirmation sent to guest" },
    ]
  },
  {
    id: "booking",
    title: "Experience Bookings",
    description: "Messages for experience booking flow",
    templates: [
      { key: "msg_vendor_broadcast", twilio_key: "twilio_tpl_vendor_broadcast", label: "Broadcast → Vendors", description: "Broadcasted to all eligible vendors" },
      { key: "msg_booking_staff", twilio_key: "twilio_tpl_booking_staff", label: "New Booking → Monkey (Manager)", description: "Sent to manager for approval" },
      { key: "msg_booking_staff_hi", twilio_key: "twilio_tpl_booking_staff_hi", label: "New Booking → Monkey (Hindi)", description: "Sent to manager for approval (Hindi)" },
      { key: "msg_booking_guest_approved", twilio_key: "twilio_tpl_booking_guest_approved", label: "Booking Approved → Guest", description: "Approval with payment link" },
      { key: "msg_booking_guest_rejected", twilio_key: "twilio_tpl_booking_guest_rejected", label: "Booking Rejected → Guest", description: "Sent if booking cannot be confirmed" },
      { key: "msg_booking_payment_failed_guest", twilio_key: "twilio_tpl_booking_payment_failed_guest", label: "Payment Failed → Guest", description: "Sent to guest if online booking payment fails" },
      { key: "msg_booking_payment_staff", twilio_key: "twilio_tpl_booking_payment_staff", label: "Payment Confirmed → Monkey", description: "Notifies manager of payment" },
      { key: "msg_booking_payment_staff_hi", twilio_key: "twilio_tpl_booking_payment_staff_hi", label: "Payment Confirmed → Monkey (Hindi)", description: "Notifies manager of payment (Hindi)" },
      { key: "msg_booking_payment_guest", twilio_key: "twilio_tpl_booking_payment_success_guest", label: "Payment Confirmed → Guest", description: "Payment confirmation to guest" },
      { key: "msg_booking_guest_delivered", twilio_key: "twilio_tpl_booking_guest_delivered", label: "Experience Feedback → Guest", description: "Feedback request sent after experience completion" },
    ]
  },
  {
    id: "request",
    title: "Service Requests",
    description: "Messages for guest service requests",
    templates: [
      { key: "msg_request_staff", twilio_key: "twilio_tpl_request_staff", label: "New Request → Monkey (Manager)", description: "Sent to manager when guest raises request" },
      { key: "msg_request_guest_acknowledged", twilio_key: "twilio_tpl_request_guest_acknowledged", label: "Request Acknowledged → Guest", description: "Sent to guest when request is in progress" },
      { key: "msg_request_guest_resolved", twilio_key: "twilio_tpl_request_guest_resolved", label: "Request Resolved → Guest", description: "Sent to guest when request is completed" },
    ]
  },
  {
    id: "checkin",
    title: "Guest Check-in & Welcome",
    description: "Messages sent to guest and manager upon arrival check-in",
    templates: [
      { key: "guest_checkin", twilio_key: "twilio_tpl_guest_welcome", label: "Welcome & Portal Link → Guest", description: "Sent to guest with personal portal URL upon check-in" },
      { key: "guest_checkin_manager_notification", twilio_key: "twilio_tpl_manager_checkin_alert", label: "Check-in Alert → Manager", description: "Sent to managers notifying new guest arrival" },
    ]
  },
  {
    id: "broadcast",
    title: "Guest Broadcasts",
    description: "Templates for sending announcements to all currently checked-in guests",
    templates: [
      { key: "msg_broadcast_event", twilio_key: "twilio_tpl_broadcast_event", label: "Event Announcement → All Guests", description: "Sent to checked-in guests for events — variables: {{1}} guest name, {{2}} event name, {{3}} time" },
      { key: "msg_broadcast_alert", twilio_key: "twilio_tpl_broadcast_alert", label: "Information / Alert → All Guests", description: "Sent to checked-in guests for updates like road blockage, electricity cut — variables: {{1}} guest name, {{2}} message" },
    ]
  },
  {
    id: "auth",
    title: "Authentication & Security",
    description: "Security and account recovery OTP messages",
    templates: [
      { key: "msg_food_otp", twilio_key: "twilio_tpl_food_otp", label: "Guest Food OTP → Guest", description: "4-digit OTP code sent to verify guest mobile number before placing food order" },
      { key: "msg_password_reset_otp", twilio_key: "twilio_tpl_password_reset_otp", label: "Password Reset OTP → Staff", description: "6-digit OTP code sent for account password reset" },
    ]
  },
];

const AVAILABLE_VARIABLES = [
  { name: "{guest_name}", description: "Guest's name" },
  { name: "{room_number}", description: "Room number" },
  { name: "{order_id}", description: "Order/Booking/Request ID" },
  { name: "{property_name}", description: "Property name" },
  { name: "{whatsapp}", description: "Guest's WhatsApp number" },
  { name: "{total}", description: "Order total amount" },
  { name: "{items_list}", description: "List of ordered items" },
  { name: "{notes_section}", description: "Notes if provided" },
  { name: "{experience_title}", description: "Experience name" },
  { name: "{experience_price}", description: "Experience price" },
  { name: "{category}", description: "Request category" },
  { name: "{message}", description: "Request message" },
  { name: "{payment_link}", description: "Razorpay payment link" },
  { name: "{fulfillment_code}", description: "4-digit verification code" },
  { name: "{amount}", description: "Payment amount" },
  { name: "{portal_url}", description: "Guest portal link" },
  { name: "{email}", description: "Guest email address" },
  { name: "{otp}", description: "One-time password code" },
];

const DEFAULT_TWILIO_SIDS = {
  twilio_tpl_food_order_staff: "",
  twilio_tpl_food_pay_success_staff: "",
  twilio_tpl_food_pay_success_manager: "",
  twilio_tpl_order_approved_guest: "",
  twilio_tpl_order_rejected_guest: "",
  twilio_tpl_order_payment_failed_guest: "",
  twilio_tpl_order_payment_success_guest: "",
  twilio_tpl_vendor_broadcast: "",
  twilio_tpl_booking_staff: "",
  twilio_tpl_booking_guest_approved: "",
  twilio_tpl_booking_guest_rejected: "",
  twilio_tpl_booking_payment_failed_guest: "",
  twilio_tpl_booking_payment_staff: "",
  twilio_tpl_booking_payment_success_guest: "",
  twilio_tpl_booking_guest_delivered: "",
  twilio_tpl_request_staff: "",
  twilio_tpl_request_guest_acknowledged: "",
  twilio_tpl_request_guest_resolved: "",
  twilio_tpl_guest_welcome: "",
  twilio_tpl_manager_checkin_alert: "",
  twilio_tpl_food_pay_failed_staff: "",
  twilio_tpl_broadcast_event: "",
  twilio_tpl_broadcast_alert: "",
  twilio_tpl_password_reset_otp: "",
  twilio_tpl_food_otp: "",
};

const SAMPLE_TEMPLATE_VARIABLES = {
  msg_food_order_staff: { "1": "Aarav Sharma", "2": "204", "3": "2x Masala Chai, 1x Veg Poha", "4": "Less spicy, extra hot" },
  msg_food_order_staff_hi: { "1": "Aarav Sharma", "2": "204", "3": "2x Masala Chai, 1x Veg Poha", "4": "Less spicy, extra hot" },
  msg_food_pay_success_staff: { "1": "#HM-8812 (Aarav Sharma)", "2": "204", "3": "2x Masala Chai, 1x Veg Poha", "4": "250" },
  msg_food_pay_success_staff_hi: { "1": "#HM-8812 (Aarav Sharma)", "2": "204", "3": "2x Masala Chai, 1x Veg Poha", "4": "250" },
  msg_food_pay_success_manager: { "1": "#HM-8812 (Aarav Sharma)", "2": "204", "3": "2x Masala Chai, 1x Veg Poha", "4": "250" },
  msg_food_order_guest: { "1": "Aarav Sharma", "2": "HM-8812", "3": "250", "4": "https://rzp.io/i/test_link" },
  msg_food_order_reception: { "1": "Aarav Sharma", "2": "HM-8812", "3": "250" },
  msg_order_rejected_guest: { "1": "Aarav Sharma", "2": "HM-8812" },
  msg_order_payment_failed_guest: { "1": "Aarav Sharma", "2": "HM-8812" },
  msg_food_pay_failed_staff: { "1": "HM-8812", "2": "Aarav Sharma", "3": "204", "4": "250" },
  msg_broadcast_event: { "1": "Aarav Sharma", "2": "Holi Night 🎉", "3": "8:00 PM" },
  msg_broadcast_alert: { "1": "Aarav Sharma", "2": "Road blockage on MG Road — allow extra 20 mins if heading out." },
  msg_order_payment_success_guest: { "1": "Aarav Sharma", "2": "HM-8812" },
  msg_vendor_broadcast: { "1": "EXP-3301", "2": "Sunrise Boat Tour", "3": "Aarav Sharma", "4": "204" },
  msg_booking_staff: { "1": "Aarav Sharma", "2": "Sunrise Boat Tour", "3": "Date: 2026-09-20 | Slots: 6:00 AM", "4": "Vegetarian" },
  msg_booking_staff_hi: { "1": "Aarav Sharma", "2": "Sunrise Boat Tour", "3": "Date: 2026-09-20 | Slots: 6:00 AM", "4": "Vegetarian" },
  msg_booking_guest_approved: { "1": "Aarav Sharma", "2": "Sunrise Boat Tour", "3": "500", "4": "https://rzp.io/i/test_link" },
  msg_booking_guest_rejected: { "1": "Aarav Sharma", "2": "Sunrise Boat Tour" },
  msg_booking_payment_failed_guest: { "1": "Aarav Sharma", "2": "Sunrise Boat Tour" },
  msg_booking_payment_staff: { "1": "Aarav Sharma", "2": "Sunrise Boat Tour" },
  msg_booking_payment_staff_hi: { "1": "Aarav Sharma", "2": "Sunrise Boat Tour" },
  msg_booking_payment_guest: { "1": "Aarav Sharma", "2": "500", "3": "Sunrise Boat Tour", "4": "EXP-3301" },
  msg_booking_payment_success_guest: { "1": "Aarav Sharma", "2": "500", "3": "Sunrise Boat Tour", "4": "EXP-3301" },
  msg_booking_guest_delivered: { "1": "Aarav Sharma", "2": "Sunrise Boat Tour" },
  msg_request_staff: { "1": "Aarav Sharma", "2": "Housekeeping", "3": "204", "4": "Fresh towels and bedsheet change" },
  msg_request_guest_acknowledged: { "1": "Aarav Sharma", "2": "Housekeeping", "3": "REQ-1044" },
  msg_request_guest_resolved: { "1": "Aarav Sharma", "2": "Housekeeping" },
  guest_checkin: { "1": "Aarav Sharma", "2": "https://app.hiddenmonkeyhostels.com/varanasi" },
  guest_checkin_manager_notification: { "1": "Aarav Sharma", "2": "+919876543210", "3": "aarav@gmail.com" },
  msg_password_reset_otp: { "1": "582910" },
  msg_food_otp: { "1": "8429", "2": "Hidden Monkey Stays" },
};

export default function AdminMessageTemplates() {
  const [properties, setProperties] = useState([]);
  const [selectedProperty, setSelectedProperty] = useState("");
  const [templates, setTemplates] = useState({});
  const [globalTemplates, setGlobalTemplates] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Live Testing State
  const [testPhoneNumber, setTestPhoneNumber] = useState(() => {
    try {
      return localStorage.getItem("hm_test_phone") || "";
    } catch {
      return "";
    }
  });
  const [activeTestTemplate, setActiveTestTemplate] = useState(null);
  const [testModalOpen, setTestModalOpen] = useState(false);
  const [testSending, setTestSending] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [testVariables, setTestVariables] = useState({});

  useEffect(() => {
    try {
      if (testPhoneNumber) {
        localStorage.setItem("hm_test_phone", testPhoneNumber);
      }
    } catch {
      // ignore localStorage quota errors
    }
  }, [testPhoneNumber]);

  useEffect(() => {
    loadProperties();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (selectedProperty) {
      loadTemplates();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProperty]);

  const loadProperties = async () => {
    try {
      const data = await getProperties();
      setProperties(data);
      if (data.length > 0) {
        setSelectedProperty(data[0].property_id);
      }
    } catch (error) {
      toast.error("Failed to load properties");
    }
  };

  const loadTemplates = async () => {
    setLoading(true);
    try {
      // Fetch both specific property and global settings
      const [propertyData, globalData] = await Promise.all([
        getSettings(selectedProperty).catch(() => ({})),
        selectedProperty !== "global" ? getSettings("global").catch(() => ({})) : Promise.resolve({})
      ]);
      
      const loadedTemplates = {};
      const parsedGlobal = {};
      TEMPLATE_CATEGORIES.forEach(cat => {
        cat.templates.forEach(tpl => {
          parsedGlobal[tpl.key] = globalData[tpl.key] || DEFAULT_TEMPLATES[tpl.key] || "";
          loadedTemplates[tpl.key] = propertyData[tpl.key] || parsedGlobal[tpl.key];
          if (tpl.twilio_key) {
            parsedGlobal[tpl.twilio_key] = globalData[tpl.twilio_key] || DEFAULT_TWILIO_SIDS[tpl.twilio_key] || "";
            loadedTemplates[tpl.twilio_key] = propertyData[tpl.twilio_key] || parsedGlobal[tpl.twilio_key];
          }
        });
      });
      setGlobalTemplates(parsedGlobal);
      setTemplates(loadedTemplates);
    } catch (error) {
      const defaultVals = { ...DEFAULT_TEMPLATES };
      Object.assign(defaultVals, DEFAULT_TWILIO_SIDS);
      setTemplates(defaultVals);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await saveSettings({
        property_id: selectedProperty,
        ...templates
      });
      toast.success("Message templates saved!");
    } catch (error) {
      toast.error("Failed to save templates");
    } finally {
      setSaving(false);
    }
  };

  const resetTemplate = (key, twilio_key) => {
    if (selectedProperty !== "global") {
      setTemplates(prev => ({
        ...prev,
        [key]: globalTemplates[key] || DEFAULT_TEMPLATES[key] || "",
        ...(twilio_key ? { [twilio_key]: globalTemplates[twilio_key] || "" } : {})
      }));
      toast.success("Template synced from Global. Click 'Save Templates' to apply.");
    } else {
      setTemplates(prev => ({
        ...prev,
        [key]: DEFAULT_TEMPLATES[key] || "",
        ...(twilio_key ? { [twilio_key]: DEFAULT_TWILIO_SIDS[twilio_key] || "" } : {})
      }));
      toast.success("Template reset to factory default");
    }
  };

  const resetAllTemplates = async () => {
    if (selectedProperty === "global") {
      const defaultVals = {};
      TEMPLATE_CATEGORIES.forEach(cat => {
        cat.templates.forEach(tpl => {
          defaultVals[tpl.key] = DEFAULT_TEMPLATES[tpl.key] || "";
          if (tpl.twilio_key) defaultVals[tpl.twilio_key] = DEFAULT_TWILIO_SIDS[tpl.twilio_key] || "";
        });
      });
      setTemplates(defaultVals);
      toast.success("Global templates reset to factory defaults");
    } else {
      try {
        // Wipe all template and SID overrides from this property in the database
        // so they automatically inherit 100% from Global — no Save click needed
        await api.delete(`/settings/${selectedProperty}/templates`);
        toast.success(`✅ Synced! All templates & SIDs on ${selectedProperty} now inherit from Global.`);
        // Reload templates so the UI reflects the actual DB state
        await loadTemplates();
      } catch (err) {
        toast.error(err.response?.data?.detail || "Failed to sync with Global");
      }
    }
  };

  const syncAllPropertiesWithGlobal = async () => {
    setSaving(true);
    try {
      // 1. Save current Global templates
      await saveSettings({
        property_id: "global",
        ...templates
      });
      // 2. Wipe template and SID overrides on all properties in DB
      await api.delete("/settings/all-properties/templates");
      toast.success("✅ Master templates saved & synced to all properties!");
      await loadTemplates();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to sync all properties");
    } finally {
      setSaving(false);
    }
  };


  const handleOpenTestModal = (template) => {
    setActiveTestTemplate(template);
    const initialVars = SAMPLE_TEMPLATE_VARIABLES[template.key] 
      ? { ...SAMPLE_TEMPLATE_VARIABLES[template.key] }
      : { "1": "Aarav Sharma" };
    setTestVariables(initialVars);
    setTestResult(null);
    setTestModalOpen(true);
  };

  const handleSendTest = async () => {
    const rawNumber = testPhoneNumber.trim();
    if (!rawNumber) {
      toast.error("Please enter a test recipient phone number");
      return;
    }
    if (!activeTestTemplate) return;

    setTestSending(true);
    setTestResult(null);
    try {
      const payload = {
        property_id: selectedProperty,
        template_key: activeTestTemplate.key,
        phone_number: rawNumber,
        twilio_sid: templates[activeTestTemplate.twilio_key] || "",
        custom_message: templates[activeTestTemplate.key] || "",
        custom_variables: testVariables
      };

      const res = await api.post("/admin/templates/test", payload);
      setTestResult(res.data);
      if (res.data.success) {
        if (res.data.delivery_mode === "template") {
          toast.success(`WhatsApp message sent via Meta Approved Template!`);
        } else {
          toast.warning(`Sent via WhatsApp plain text fallback.`);
        }
      } else {
        toast.error(res.data.error || "Failed to deliver WhatsApp message");
      }
    } catch (err) {
      const errorMsg = err.response?.data?.detail || err.message || "Failed to dispatch test message";
      setTestResult({
        success: false,
        error: errorMsg
      });
      toast.error(errorMsg);
    } finally {
      setTestSending(false);
    }
  };

  const renderPreviewText = () => {
    if (!activeTestTemplate) return "";
    const rawTemplate = templates[activeTestTemplate.key] || DEFAULT_TEMPLATES[activeTestTemplate.key] || "";
    let rendered = rawTemplate;
    Object.entries(testVariables).forEach(([k, v]) => {
      rendered = rendered.split(`{{${k}}}`).join(v);
      rendered = rendered.split(`{${k}}`).join(v);
    });
    const namedMap = {
      guest_name: testVariables["1"] || "Aarav Sharma",
      room_number: testVariables["2"] || "204",
      order_id: testVariables["2"] || "HM-8812",
      items: testVariables["3"] || "2x Masala Chai, 1x Veg Poha",
      items_list: testVariables["3"] || "2x Masala Chai, 1x Veg Poha",
      amount: testVariables["4"] || "250",
      total: testVariables["4"] || "250",
      notes: testVariables["4"] || "Less spicy, extra hot",
      special_requests: testVariables["4"] || "Less spicy, extra hot",
      experience_title: testVariables["2"] || "Sunrise Boat Tour",
      date_slots: testVariables["3"] || "Date: 2026-09-20 | Slots: 6:00 AM",
      category: testVariables["2"] || "Housekeeping",
      message: testVariables["4"] || "Fresh towels requested",
      portal_url: testVariables["2"] || `https://app.hiddenmonkeyhostels.com/${selectedProperty || "varanasi"}`,
      whatsapp: testVariables["2"] || "+919876543210",
      email: testVariables["3"] || "guest@test.com",
      otp: testVariables["1"] || "582910",
      payment_link: testVariables["4"] || "https://rzp.io/i/test_link",
      fulfillment_code: "8429",
      reason: "Capacity reached",
      property_name: "Hidden Monkey"
    };
    Object.entries(namedMap).forEach(([k, v]) => {
      rendered = rendered.split(`{${k}}`).join(v);
    });
    return rendered;
  };

  return (
    <AdminLayout title="Message Templates">
      <div className="space-y-6" data-testid="admin-message-templates">
        {/* Property Selector & Actions */}
        <Card>
          <CardContent className="p-6">
            <div className="flex flex-col md:flex-row gap-4 items-start md:items-end justify-between">
              <div className="w-full md:w-64">
                <Label className="text-[#264653] mb-2 block">Property</Label>
                <Select value={selectedProperty} onValueChange={setSelectedProperty}>
                  <SelectTrigger className="hm-input" data-testid="template-property-select">
                    <SelectValue placeholder="Select property" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="global">
                      <div className="flex items-center gap-2 font-semibold text-[#2A9D8F]">
                        <Globe className="w-4 h-4" />
                        Global (All Properties)
                      </div>
                    </SelectItem>
                    {properties.map(p => (
                      <SelectItem key={p.property_id} value={p.property_id}>
                        {p.property_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="flex flex-wrap gap-2.5">
                <Button 
                  variant="outline" 
                  onClick={resetAllTemplates}
                  className="border-[#FFC107] text-[#FFC107] hover:bg-[#FFC107]/10"
                >
                  <RefreshCw className="w-4 h-4 mr-2" />
                  {selectedProperty === "global" ? "Reset to Defaults" : "Sync with Global"}
                </Button>
                {selectedProperty === "global" && (
                  <Button
                    onClick={syncAllPropertiesWithGlobal}
                    disabled={saving}
                    className="bg-[#264653] hover:bg-[#1d3540] text-white"
                    title="Save Global templates and sync them across Varanasi, Darjeeling Hostel, and Darjeeling Home"
                  >
                    <Globe className="w-4 h-4 mr-2" />
                    {saving ? "Syncing..." : "Sync All Properties"}
                  </Button>
                )}
                <Button 
                  onClick={handleSave} 
                  disabled={saving}
                  className="btn-primary"
                  data-testid="save-templates-btn"
                >
                  <Save className="w-4 h-4 mr-2" />
                  {saving ? "Saving..." : selectedProperty === "global" ? "Save Global" : "Save Templates"}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Test Recipient Phone Number */}
        <Card className="border-[#2A9D8F]/30 bg-[#2A9D8F]/5">
          <CardContent className="p-4 sm:p-6 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-[#2A9D8F]/20 flex items-center justify-center text-[#2A9D8F] shrink-0">
                <Phone className="w-5 h-5" />
              </div>
              <div>
                <h4 className="font-semibold text-[#264653] text-sm sm:text-base">Global WhatsApp Test Recipient</h4>
                <p className="text-xs text-[#6B705C]">
                  Phone number used across all template tests. Saved in your browser. Supports Indian 10-digit mobile or international E.164 (+91...).
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2 w-full md:w-auto">
              <Input
                value={testPhoneNumber}
                onChange={(e) => setTestPhoneNumber(e.target.value)}
                placeholder="+91 98765 43210"
                className="w-full md:w-64 font-mono text-sm bg-white"
                data-testid="test-phone-input"
              />
            </div>
          </CardContent>
        </Card>

        {/* Variables Reference */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-lg text-[#2A9D8F]">Available Variables</CardTitle>
            <CardDescription>Use these placeholders in your templates - they'll be replaced with actual values</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {AVAILABLE_VARIABLES.map(v => (
                <span 
                  key={v.name} 
                  className="px-3 py-1 bg-[#2A9D8F]/10 text-[#2A9D8F] rounded-full text-sm cursor-help"
                  title={v.description}
                >
                  {v.name}
                </span>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Templates by Category */}
        {loading ? (
          <div className="space-y-4">
            {[1, 2, 3].map(i => (
              <Skeleton key={i} className="h-32 w-full" />
            ))}
          </div>
        ) : (
          <Accordion type="multiple" className="space-y-4">
            {TEMPLATE_CATEGORIES.map(category => (
              <AccordionItem 
                key={category.id} 
                value={category.id}
                className="bg-white rounded-xl border border-[#E8E4D9] overflow-hidden"
              >
                <AccordionTrigger className="px-6 py-4 hover:no-underline hover:bg-[#F8F7F4]">
                  <div className="flex items-center gap-3 text-left">
                    <MessageSquare className="w-5 h-5 text-[#2A9D8F]" />
                    <div>
                      <h3 className="font-semibold text-[#264653]">{category.title}</h3>
                      <p className="text-sm text-[#6B705C]">{category.description}</p>
                    </div>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="px-6 pb-6">
                  <div className="space-y-6">
                    {category.templates.map(template => (
                      <div key={template.key} className="border border-[#E8E4D9] rounded-lg p-4">
                        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2 mb-3">
                          <div>
                            <div className="flex items-center gap-2">
                              <Label className="text-[#264653] font-medium text-base">{template.label}</Label>
                              {template.twilio_key && templates[template.twilio_key] && (
                                <Badge variant="outline" className="text-[10px] bg-[#2A9D8F]/10 text-[#2A9D8F] border-[#2A9D8F]/30">
                                  Meta Approved SID
                                </Badge>
                              )}
                            </div>
                            <p className="text-xs text-[#6B705C]">{template.description}</p>
                          </div>
                          <div className="flex items-center gap-2 self-end sm:self-auto">
                            <Button
                              type="button"
                              size="sm"
                              onClick={() => handleOpenTestModal(template)}
                              className="bg-[#2A9D8F] hover:bg-[#2A9D8F]/90 text-white text-xs h-8 px-3 flex items-center gap-1.5 shadow-sm"
                              data-testid={`test-template-btn-${template.key}`}
                            >
                              <Send className="w-3.5 h-3.5" />
                              Test Template
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => resetTemplate(template.key, template.twilio_key)}
                              className="text-[#6B705C] hover:text-[#2A9D8F] text-xs h-8"
                              title={selectedProperty === "global" ? "Reset to factory default" : "Sync this template & SID from Global"}
                            >
                              <RefreshCw className="w-3 h-3 mr-1" />
                              {selectedProperty === "global" ? "Reset" : "Sync from Global"}
                            </Button>
                          </div>
                        </div>
                        {template.twilio_key && (
                          <div className="mb-3">
                            <Label className="text-xs text-[#264653]">Twilio Template SID (Optional)</Label>
                            <Input
                              value={templates[template.twilio_key] || ""}
                              onChange={(e) => setTemplates(prev => ({ ...prev, [template.twilio_key]: e.target.value }))}
                              placeholder="HX..."
                              className="font-mono text-xs h-8"
                            />
                          </div>
                        )}
                        <div>
                          <Label className="text-xs text-[#264653]">Fallback Message Text</Label>
                          <textarea
                            value={templates[template.key] || ""}
                            onChange={(e) => setTemplates(prev => ({ ...prev, [template.key]: e.target.value }))}
                            className="w-full h-32 mt-1 p-3 border border-[#E8E4D9] rounded-lg font-mono text-sm resize-y focus:ring-2 focus:ring-[#2A9D8F] focus:border-transparent"
                            placeholder="Enter message template..."
                            data-testid={`template-${template.key}`}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        )}

        {/* Live Template Test Dialog */}
        <Dialog open={testModalOpen} onOpenChange={setTestModalOpen}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <div className="flex items-center gap-2">
                <span className="p-2 rounded-lg bg-[#2A9D8F]/10 text-[#2A9D8F]">
                  <Send className="w-5 h-5" />
                </span>
                <div>
                  <DialogTitle className="text-xl text-[#264653]">
                    Test WhatsApp Template
                  </DialogTitle>
                  <DialogDescription className="text-xs text-[#6B705C]">
                    Dispatch a live WhatsApp test message via Twilio to verify formatting and delivery.
                  </DialogDescription>
                </div>
              </div>
            </DialogHeader>

            {activeTestTemplate && (
              <div className="space-y-4 py-2">
                {/* Meta details banner */}
                <div className="p-3 rounded-lg border border-[#E8E4D9] bg-[#F8F7F4] flex flex-wrap items-center justify-between gap-2 text-xs">
                  <div>
                    <span className="font-semibold text-[#264653] block">{activeTestTemplate.label}</span>
                    <span className="text-[#6B705C] font-mono">{activeTestTemplate.key}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    {activeTestTemplate.twilio_key && templates[activeTestTemplate.twilio_key] ? (
                      <Badge className="bg-[#2A9D8F] text-white">
                        SID: {templates[activeTestTemplate.twilio_key]}
                      </Badge>
                    ) : (
                      <Badge variant="outline" className="text-amber-700 bg-amber-50 border-amber-300">
                        Plain Text Fallback
                      </Badge>
                    )}
                  </div>
                </div>

                {/* Recipient Phone Number */}
                <div className="space-y-1.5">
                  <Label htmlFor="modal-test-phone" className="text-sm font-medium text-[#264653] flex items-center justify-between">
                    <span>Recipient Phone Number</span>
                    <span className="text-xs text-[#6B705C] font-normal">e.g. +91 98765 43210 or 9876543210</span>
                  </Label>
                  <div className="relative">
                    <Phone className="w-4 h-4 text-[#6B705C] absolute left-3 top-3" />
                    <Input
                      id="modal-test-phone"
                      value={testPhoneNumber}
                      onChange={(e) => setTestPhoneNumber(e.target.value)}
                      placeholder="+919876543210"
                      className="pl-9 font-mono"
                      data-testid="modal-test-phone-input"
                    />
                  </div>
                </div>

                {/* Sample / Custom Template Variables */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label className="text-sm font-medium text-[#264653]">
                      Template Parameters (&#123;&#123;1&#125;&#125;, &#123;&#123;2&#125;&#125;, ...)
                    </Label>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="text-xs text-[#2A9D8F] h-6 px-2 hover:bg-[#2A9D8F]/10"
                      onClick={() => {
                        const resetVars = SAMPLE_TEMPLATE_VARIABLES[activeTestTemplate.key]
                          ? { ...SAMPLE_TEMPLATE_VARIABLES[activeTestTemplate.key] }
                          : { "1": "Aarav Sharma" };
                        setTestVariables(resetVars);
                      }}
                    >
                      <RefreshCw className="w-3 h-3 mr-1" />
                      Reset to Sample
                    </Button>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 p-3 rounded-lg border border-[#E8E4D9] bg-white">
                    {Object.keys(testVariables).length === 0 ? (
                      <p className="text-xs text-[#6B705C] italic col-span-2">This template takes no dynamic variables.</p>
                    ) : (
                      Object.keys(testVariables).sort((a, b) => Number(a) - Number(b)).map((key) => (
                        <div key={key} className="space-y-1">
                          <Label className="text-xs text-[#6B705C] font-mono">
                            Variable &#123;&#123;{key}&#125;&#125;
                          </Label>
                          <Input
                            value={testVariables[key]}
                            onChange={(e) => setTestVariables(prev => ({ ...prev, [key]: e.target.value }))}
                            className="text-xs font-mono h-8"
                            data-testid={`modal-test-var-${key}`}
                          />
                        </div>
                      ))
                    )}
                  </div>
                </div>

                {/* Live Message Preview */}
                <div className="space-y-1.5">
                  <Label className="text-sm font-medium text-[#264653] flex items-center gap-1.5">
                    <Sparkles className="w-3.5 h-3.5 text-[#2A9D8F]" />
                    Live Message Preview
                  </Label>
                  <div className="p-3 bg-[#E7F5E8] border border-[#2A9D8F]/30 rounded-lg text-xs font-mono whitespace-pre-wrap text-[#264653] shadow-inner max-h-40 overflow-y-auto">
                    {renderPreviewText()}
                  </div>
                </div>

                {/* Delivery Result Banner */}
                {testResult && (
                  <div className={`p-4 rounded-lg border text-xs space-y-1.5 ${
                    testResult.success
                      ? testResult.delivery_mode === "template"
                        ? "bg-emerald-50 border-emerald-300 text-emerald-900"
                        : "bg-amber-50 border-amber-300 text-amber-900"
                      : "bg-rose-50 border-rose-300 text-rose-900"
                  }`}>
                    <div className="flex items-center gap-2 font-semibold text-sm">
                      {testResult.success ? (
                        testResult.delivery_mode === "template" ? (
                          <>
                            <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                            <span>WhatsApp Message Sent via Meta Template!</span>
                          </>
                        ) : (
                          <>
                            <AlertTriangle className="w-4 h-4 text-amber-600" />
                            <span>WhatsApp Sent via Plain Text Fallback</span>
                          </>
                        )
                      ) : (
                        <>
                          <XCircle className="w-4 h-4 text-rose-600" />
                          <span>Dispatch Failed</span>
                        </>
                      )}
                    </div>

                    {testResult.success ? (
                      <div className="space-y-1 font-mono text-[11px] pt-1">
                        <div><strong>Twilio Message SID:</strong> {testResult.message_sid || "N/A"}</div>
                        <div><strong>Recipient:</strong> {testResult.to}</div>
                        <div><strong>Mode:</strong> {testResult.delivery_mode === "template" ? "Twilio Meta Approved Template" : "Standard WhatsApp Session / Fallback"}</div>
                        {testResult.delivery_mode !== "template" && (
                          <div className="text-amber-800 text-[10px] mt-1 font-sans">
                            Note: Twilio template SID was either not configured, still pending approval, or rejected by Meta for this recipient. The fallback plain text body was dispatched instead.
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="font-mono text-[11px] pt-1">
                        <strong>Error:</strong> {testResult.error}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            <DialogFooter className="gap-2 sm:gap-0">
              <Button
                type="button"
                variant="outline"
                onClick={() => setTestModalOpen(false)}
                disabled={testSending}
              >
                Close
              </Button>
              <Button
                type="button"
                onClick={handleSendTest}
                disabled={testSending}
                className="bg-[#2A9D8F] hover:bg-[#2A9D8F]/90 text-white flex items-center gap-1.5"
                data-testid="confirm-send-test-btn"
              >
                {testSending ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Sending Test...
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4" />
                    Send WhatsApp Test
                  </>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
}
